/**
 * Normalized Scoring System for Matchmaking
 * 
 * All scoring dimensions return values between 0-100, then weighted
 * to produce a final score. This makes the system:
 * - Easier to tune (adjust weights without breaking other dimensions)
 * - More predictable (no order-of-magnitude differences)
 * - More debuggable (can see contribution of each dimension)
 * - More maintainable (clear separation of concerns)
 * 
 * Architecture:
 * Each dimension returns 0-100 where:
 * - 100 = Perfect/ideal match
 * - 50 = Acceptable/neutral
 * - 0 = Poor but legal match
 * - -Infinity = Hard block (illegal match)
 */

import { debugLog } from './matchmakingConfig.js';
import { MATCHMAKING_CONFIG } from './matchmakingConfig.js';
import { resolveCategoryLabel, getGameTypeKey } from './categoryResolver.js';
import {
  countPersonalConsecutiveCourtStreak,
  getPersonalGamesForPlayer,
  getPairInteractionKey,
  parseGameTeamsFromHistoryEntry,
  getJustFinishedPlayerIds,
  getImmediateReselectStreak,
} from './sessionMatchModel.js';
import { getSinglesPairingPolicy, MATCH_FORMAT_SINGLES } from './competitiveMatchFormat.js';
import { getSinglesEloForComputation } from './playerElo.js';
import { pairAllowedByPolicy } from './singlesCourtEngine.js';
import { getLevelsEloForMatchmaking, isSessionMensDoublesEloExemptLineup } from './competitiveGender.js';
import {
  computePlayerDebts,
  computeTierMedians,
  isSessionDebtModelEnabled,
} from './sessionDebt.js';

export { getJustFinishedPlayerIds, getImmediateReselectStreak };

/**
 * Default scoring configuration
 * These are the baseline settings that work well for most sessions
 * Hosts can override these per-session to match their preferences
 * 
 * All weights should sum to 1.0 for clarity
 */
export const DEFAULT_SCORING_CONFIG = {
  // Dimension weights - defines relative importance of each factor
  weights: {
    fairness: 0.35,      // 35% - Games played balance (primary session fairness lever)
    eloBalance: 0.20,    // 20% - Competitive balance
    interactions: 0.25,  // 25% - Variety/freshness (pod overlap, repeats — soft only, never deadlocks)
    gameType: 0.20,      // 20% - Balanced game types + variety (categories removed; kept as hard block only)
  },
  
  // Philosophy: Fairness is weighted highest so lower-game players are strongly preferred
  // when valid combinations exist. ELO and variety still matter but cannot easily override
  // a large games-played gap.
};

/** Normalized weights for competitive singles (slightly more fairness vs game type) */
export const DEFAULT_SCORING_CONFIG_SINGLES = {
  weights: {
    fairness: 0.42,
    eloBalance: 0.20,
    interactions: 0.20,
    gameType: 0.18,
  },
};

/**
 * Configuration for normalized scoring thresholds
 */
export const NORMALIZED_SCORING_CONFIG = {
  fairness: {
    // Blended deviation from minGames (avg catches spread; max catches one high-game outlier)
    blendAvgWeight: 0.4,
    blendMaxWeight: 0.6,
    // Additive penalty applied after weighted dimensions (per player, per game above session min)
    penaltyPerGameAboveMin: 15,
    // Additive rest penalty (post-score upcoming refill): just-finished player going straight back on
    penaltyImmediateReselect: 20,
    // Extra additive amount per additional immediate re-select (streak 2 = third consecutive court)
    penaltyRepeatReselect: 55,
    // Session-history games allowed between personal games and still count as an immediate B2B
    immediateReselectGapMax: 1,
    // Composite deviation thresholds → 0-100 fairness dimension score
    perfect: 0,      // All at min → 100 points
    good: 0.5,       // Composite deviation ≤ 0.5 → 75+ points
    acceptable: 1.5, // Composite deviation ≤ 1.5 → 50+ points
    poor: 2.5,       // Composite deviation ≤ 2.5 → 25+ points
    // > 2.5 deviation → 0-25 points
  },
  
  eloBalance: {
    // ELO difference thresholds (lower is better)
    perfect: 50,     // < 50 ELO diff → 100 points
    good: 150,       // < 150 ELO diff → 75+ points
    acceptable: 300, // < 300 ELO diff → 50+ points
    poor: 500,       // < 500 ELO diff → 25+ points
    // > 500 ELO diff → 0-25 points
    
    // Badminton-specific weights by game type
    levels: {
      teamBalanceWeight: 0.60,     // Team average balance
      intraTeamBalanceWeight: 0.40, // Partner skill similarity
      acceptableIntraTeamGap: 200,  // 200 point gap is acceptable within a team
    },
    
    mixed: {
      teamBalanceWeight: 0.50,      // Team average balance
      genderBalanceWeight: 0.50,    // Male vs male, female vs female balance
    },
    
    allTypes: {
      teamBalanceWeight: 1.0,       // Just team average balance
    },
  },
  
  interactions: {
    // Recency decay factor - how quickly old interactions matter less
    decayFactor: 0.7,           // Each game back reduces weight by 30%
    recentGameWindow: 5,        // Consider last 5 games
    
    // Thresholds for interaction percentages (of total possible)
    low: 0.15,       // < 15% interactions → 100 points
    moderate: 0.25,  // < 25% interactions → 75+ points
    high: 0.35,      // < 35% interactions → 50+ points
    veryHigh: 0.50,  // < 50% interactions → 25+ points
    // > 50% interactions → 0-25 points
    
    // Consecutive interaction penalties (recency-weighted)
    consecutive2Penalty: 30,     // -30 points for 2 consecutive
    consecutive3PlusPenalty: 60, // -60 points for 3+ consecutive
    // Personal last-game pod overlap (soft, always on — per-player last doubles roster)
    overlap3Penalty: 30,         // −30 per player when 3/4 of proposed court were in that player's last game
    overlap4Penalty: 65,         // −65 per player when 4/4 match that player's last game
    podOverlapMaxPenalty: 100,   // Cap total pod-overlap deduction on the interactions dimension
    exactIdenticalTeamPenalty: 35, // Extra soft penalty when teams/sides match a prior game exactly
    // Per-player session variety: avg fraction of on-court others never shared a court with (with or against)
    sessionPlayerNoveltyMaxBonus: 22, // Add avgFraction × this to interactions dimension (0 = disabled)
  },
  
  gameType: {
    balanced: 100,        // Balanced matchup (mixed vs mixed)
    suboptimal: 50,       // Allowed but not ideal (mens vs mixed)
    unbalanced: 0,        // Very unbalanced (mens vs womens)
    varietyWeight: 0.6,   // Blend weight for game-type variety bonus (0-1)
    // Game-level variety: bonus scales with how under-represented this type is ("games behind" second-lowest), so it scales with session length
    underRepresentedBonusPerGameBehind: 8,  // Add this many points per game this type is behind the second-lowest count
    underRepresentedBonusCap: 28,            // Cap the bonus so it doesn't dominate; host can increase gameType weight to prioritise variety
  },
  
  categories: {
    // Category gap thresholds
    perfect: 0,      // Same category → 100 points
    good: 1,         // Max 1 category gap → 75+ points
    acceptable: 2,   // Max 2 category gap → 50+ points
    poor: 3,         // Max 3 category gap → 0-25 points
    // 4+ gap → hard block (-Infinity)
  },
};

/**
 * Map a composite deviation from session min to a 0-100 fairness dimension score.
 * @param {number} compositeDeviation
 * @param {Object} config - NORMALIZED_SCORING_CONFIG.fairness thresholds
 * @returns {number}
 */
function deviationToFairnessScore(compositeDeviation, config) {
  if (compositeDeviation <= config.perfect) {
    return 100;
  }

  if (compositeDeviation <= config.good) {
    return 100 - ((compositeDeviation - config.perfect) / (config.good - config.perfect)) * 25;
  }

  if (compositeDeviation <= config.acceptable) {
    return 75 - ((compositeDeviation - config.good) / (config.acceptable - config.good)) * 25;
  }

  if (compositeDeviation <= config.poor) {
    return 50 - ((compositeDeviation - config.acceptable) / (config.poor - config.acceptable)) * 25;
  }

  return Math.max(0, 25 - ((compositeDeviation - config.poor) * 10));
}

/**
 * Calculate fairness score based on games played distribution.
 * Uses a blend of average and max deviation so one high-game player cannot hide
 * behind three at-min teammates. Returns 0-100.
 */
export function calculateFairnessScore(players, gamesMin) {
  if (!gamesMin && gamesMin !== 0) {
    gamesMin = Math.min(...players.map(p => p.effectiveGamesPlayed || p.gamesPlayedThisSession || 0));
  }

  const deviations = players.map(p => {
    const games = p.effectiveGamesPlayed || p.gamesPlayedThisSession || 0;
    return games - gamesMin;
  });

  const avgDeviation = deviations.reduce((sum, d) => sum + d, 0) / deviations.length;
  const maxDeviation = Math.max(...deviations);

  const config = NORMALIZED_SCORING_CONFIG.fairness;
  const compositeDeviation =
    (avgDeviation * config.blendAvgWeight) +
    (maxDeviation * config.blendMaxWeight);

  return deviationToFairnessScore(compositeDeviation, config);
}

/**
 * Additive penalty for players above session minimum (applied after weighted dimensions).
 * Each game above min costs penaltyPerGameAboveMin normalized points.
 * @param {Object[]} players - Player objects in the proposed match
 * @param {number|null} gamesMin - Session minimum games
 * @returns {number} Non-negative penalty to subtract from final score
 */
export function calculateGamesAboveMinPenalty(players, gamesMin) {
  if (gamesMin === null || gamesMin === undefined) {
    return 0;
  }

  const penaltyPerGame = NORMALIZED_SCORING_CONFIG.fairness.penaltyPerGameAboveMin ?? 15;
  let penalty = 0;

  for (const player of players) {
    const games = player?.effectiveGamesPlayed || player?.gamesPlayedThisSession || 0;
    const aboveMin = Math.max(0, games - gamesMin);
    penalty += aboveMin * penaltyPerGame;
  }

  return penalty;
}

/**
 * Additive fairness penalty for putting a just-finished player straight back
 * onto the upcoming generated at score time (a back-to-back).
 *
 * Streak 1 (first B2B): penaltyImmediateReselect.
 * Streak 2+ (already used that B2B):  first + repeat × (streak − 1).
 *
 * @param {Array<string|{id?: string}>} playerIds - Proposed combo player ids (or player objects)
 * @param {Object|null|undefined} sessionState
 * @returns {number} Non-negative penalty to subtract from final score
 */
export function calculateImmediateReselectPenalty(playerIds, sessionState) {
  if (!sessionState || !Array.isArray(playerIds) || playerIds.length === 0) {
    return 0;
  }

  const cfg = NORMALIZED_SCORING_CONFIG.fairness;
  const first = cfg.penaltyImmediateReselect ?? 20;
  const repeat = cfg.penaltyRepeatReselect ?? 55;
  const gapMax = cfg.immediateReselectGapMax ?? 1;

  let penalty = 0;
  for (const entry of playerIds) {
    const playerId = typeof entry === 'string' ? entry : entry?.id;
    if (!playerId) continue;
    const streak = getImmediateReselectStreak(playerId, sessionState, gapMax);
    if (streak <= 0) continue;
    penalty += first + repeat * Math.max(0, streak - 1);
  }

  return penalty;
}

/**
 * Score a scheduled game using fairness only (games played).
 * Used to compare scheduled games with matchmaking combos on the same 0-100 scale.
 * @param {Object} scheduledGame - Singles: two distinct ids across teams; doubles: four distinct ids.
 * @param {Object} playersById - Map of player id to player object (with effectiveGamesPlayed or gamesPlayedThisSession)
 * @param {number|null} gamesMin - Minimum games in session (from pool/waitlist)
 * @returns {number} 0-100 fairness score
 */
export function scoreScheduledGameFairness(scheduledGame, playersById, gamesMin) {
  if (!scheduledGame || !playersById) return 0;
  const ids = [
    scheduledGame.team1_player1_id,
    scheduledGame.team1_player2_id,
    scheduledGame.team2_player1_id,
    scheduledGame.team2_player2_id,
  ].filter(Boolean);
  const unique = [...new Set(ids)];
  // Doubles: 4 roster slots; singles: 2 distinct players
  if (unique.length === 2) {
    const players = unique.map((id) => playersById[id]).filter(Boolean);
    if (players.length !== 2) return 0;
    return calculateFairnessScore(players, gamesMin);
  }
  if (unique.length !== 4) return 0;
  const players = unique.map((id) => playersById[id]).filter(Boolean);
  if (players.length !== 4) return 0;
  return calculateFairnessScore(players, gamesMin);
}

/**
 * Calculate ELO balance score - Badminton-specific logic
 * Returns 0-100
 * 
 * @param {Array} team1Players - Team 1 player objects
 * @param {Array} team2Players - Team 2 player objects
 * @param {string} gameType - 'mixed', 'levels', or 'all' 
 */
export function calculateEloBalanceScore(team1Players, team2Players, gameType = 'levels', playerWaitMetrics = null) {
  const config = NORMALIZED_SCORING_CONFIG.eloBalance;

  // Singles 1v1: compare individual singles Elo (not team average /2)
  if (team1Players.length === 1 && team2Players.length === 1) {
    const teamDiff = Math.abs(
      getSinglesEloForComputation(team1Players[0]) - getSinglesEloForComputation(team2Players[0])
    );
    let teamBalanceScore = 100;
    if (teamDiff > config.perfect) {
      if (teamDiff <= config.good) {
        teamBalanceScore = 100 - ((teamDiff - config.perfect) / (config.good - config.perfect)) * 25;
      } else if (teamDiff <= config.acceptable) {
        teamBalanceScore = 75 - ((teamDiff - config.good) / (config.acceptable - config.good)) * 25;
      } else if (teamDiff <= config.poor) {
        teamBalanceScore = 50 - ((teamDiff - config.acceptable) / (config.poor - config.acceptable)) * 25;
      } else {
        teamBalanceScore = Math.max(0, 25 - ((teamDiff - config.poor) / 100) * 25);
      }
    }
    return teamBalanceScore;
  }
  
  // Determine game type if not provided
  const isMixed = gameType === 'mixed' || 
                  (team1Players.some(p => p.gender === 'male') && team1Players.some(p => p.gender === 'female'));
  const actualGameType = isMixed ? 'mixed' : (gameType === 'all' ? 'all' : 'levels');
  
  // Helper to get ELO for a player.
  // For levels games, mens-doubles-eligible females use their session shadow ELO.
  const _pwm = playerWaitMetrics || {};
  const getElo = (player) => {
    if (isMixed) {
      return player.mixed_elo_rating || player.mixedElo || 1200;
    }
    return getLevelsEloForMatchmaking(player?.id, { [player?.id]: player }, _pwm);
  };
  
  // 1. Team Balance Score (always calculated)
  const team1Avg = team1Players.reduce((sum, p) => sum + getElo(p), 0) / 2;
  const team2Avg = team2Players.reduce((sum, p) => sum + getElo(p), 0) / 2;
  const teamDiff = Math.abs(team1Avg - team2Avg);
  
  let teamBalanceScore = 100;
  if (teamDiff > config.perfect) {
    if (teamDiff <= config.good) {
      teamBalanceScore = 100 - ((teamDiff - config.perfect) / (config.good - config.perfect)) * 25;
    } else if (teamDiff <= config.acceptable) {
      teamBalanceScore = 75 - ((teamDiff - config.good) / (config.acceptable - config.good)) * 25;
    } else if (teamDiff <= config.poor) {
      teamBalanceScore = 50 - ((teamDiff - config.acceptable) / (config.poor - config.acceptable)) * 25;
    } else {
      teamBalanceScore = Math.max(0, 25 - ((teamDiff - config.poor) / 100) * 25);
    }
  }
  
  // 2. Game-type specific secondary scoring
  let secondaryScore = 100;
  let weights;
  
  if (actualGameType === 'mixed') {
    // MIXED: Gender-specific balance (male vs male, female vs female)
    weights = config.mixed;
    
    const team1Male = team1Players.find(p => p.gender === 'male');
    const team1Female = team1Players.find(p => p.gender === 'female');
    const team2Male = team2Players.find(p => p.gender === 'male');
    const team2Female = team2Players.find(p => p.gender === 'female');
    
    if (team1Male && team2Male && team1Female && team2Female) {
      const maleDiff = Math.abs(getElo(team1Male) - getElo(team2Male));
      const femaleDiff = Math.abs(getElo(team1Female) - getElo(team2Female));
      const avgGenderDiff = (maleDiff + femaleDiff) / 2;
      
      // Score gender balance
      if (avgGenderDiff <= config.perfect) {
        secondaryScore = 100;
      } else if (avgGenderDiff <= config.good) {
        secondaryScore = 100 - ((avgGenderDiff - config.perfect) / (config.good - config.perfect)) * 25;
      } else if (avgGenderDiff <= config.acceptable) {
        secondaryScore = 75 - ((avgGenderDiff - config.good) / (config.acceptable - config.good)) * 25;
      } else if (avgGenderDiff <= config.poor) {
        secondaryScore = 50 - ((avgGenderDiff - config.acceptable) / (config.poor - config.acceptable)) * 25;
      } else {
        secondaryScore = Math.max(0, 25 - ((avgGenderDiff - config.poor) / 100) * 25);
      }
    }
    
  } else if (actualGameType === 'levels') {
    // LEVELS: Intra-team balance (partners should have similar skill)
    weights = config.levels;
    
    const team1Diff = Math.abs(getElo(team1Players[0]) - getElo(team1Players[1]));
    const team2Diff = Math.abs(getElo(team2Players[0]) - getElo(team2Players[1]));
    const avgIntraTeamDiff = (team1Diff + team2Diff) / 2;
    
    // Score intra-team balance (200 points acceptable)
    const acceptableGap = config.levels.acceptableIntraTeamGap;
    
    if (avgIntraTeamDiff <= acceptableGap / 4) {
      secondaryScore = 100; // Very tight partnerships
    } else if (avgIntraTeamDiff <= acceptableGap / 2) {
      secondaryScore = 100 - ((avgIntraTeamDiff - acceptableGap / 4) / (acceptableGap / 4)) * 15;
    } else if (avgIntraTeamDiff <= acceptableGap) {
      secondaryScore = 85 - ((avgIntraTeamDiff - acceptableGap / 2) / (acceptableGap / 2)) * 35;
    } else {
      // Penalty increases steeply beyond acceptable gap
      secondaryScore = Math.max(0, 50 - ((avgIntraTeamDiff - acceptableGap) / acceptableGap) * 50);
    }
    
  } else {
    // ALL TYPES: Just use team balance (100% weight)
    weights = config.allTypes;
    secondaryScore = 100; // Not used since weight is 0
  }
  
  // Combine team balance and secondary score with game-type specific weights
  const finalScore = (teamBalanceScore * weights.teamBalanceWeight) +
                     (secondaryScore * (1 - weights.teamBalanceWeight));
  
  return finalScore;
}

/**
 * Calculate interaction score based on recency-weighted interaction history
 * Returns 0-100 (higher = less interaction = better) or -Infinity for hard blocks
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} sessionState - Session state with matchHistory
 * @param {Array} partnerships - Partnership objects
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 */
/**
 * Average per-player novel fraction for a proposed court.
 * For each player P: among other players on court (excluding formal partner), fraction with session co-appearance count 0.
 * @param {string[]} allPlayerIds
 * @param {Object} sessionState
 * @param {Map<string, string>} partnershipMap - playerId → partnerId
 * @returns {number} 0–1
 */
export function calculateAverageSessionPlayerNoveltyFraction(allPlayerIds, sessionState, partnershipMap) {
  if (!allPlayerIds?.length) return 0;

  const interactions = sessionState?.playerInteractions || {};
  let sumFractions = 0;

  for (const playerId of allPlayerIds) {
    const partnerId = partnershipMap?.get(playerId);
    const others = allPlayerIds.filter((id) => id !== playerId && id !== partnerId);
    if (others.length === 0) {
      sumFractions += 1;
      continue;
    }
    let novelCount = 0;
    for (const otherId of others) {
      const key = getPairInteractionKey(playerId, otherId);
      if ((interactions[key] || 0) === 0) {
        novelCount += 1;
      }
    }
    sumFractions += novelCount / others.length;
  }

  return sumFractions / allPlayerIds.length;
}

/**
 * Hard block when a pair would interact in nearly every game they play (session density).
 * Controlled by relaxation step 5 (enforceSessionInteractionDensity) as a soft penalty.
 * @returns {boolean}
 */
function wouldExceedInteractionDensityBlock(player1Id, player2Id, sessionState, hardBlocks) {
  if (hardBlocks?.enforceSessionInteractionDensity === false) {
    return false;
  }

  const player1Games = sessionState.gamesPlayedThisSession?.[player1Id] || 0;
  const player2Games = sessionState.gamesPlayedThisSession?.[player2Id] || 0;
  const minGames = Math.min(player1Games, player2Games);
  if (minGames <= 0) {
    return false;
  }

  const interactionKey = getPairInteractionKey(player1Id, player2Id);
  const currentTotalCount = sessionState.playerInteractions?.[interactionKey] || 0;
  const futureTotalCount = currentTotalCount + 1;
  const futureGames = minGames + 1;

  if (futureTotalCount >= futureGames - 1) {
    return true;
  }

  const futureTotalPercentage = (futureTotalCount / futureGames) * 100;
  const highThreshold = MATCHMAKING_CONFIG.penalties.interactions.total.highThreshold;
  return futureTotalCount >= 3 && futureTotalPercentage >= highThreshold;
}

/** @deprecated Use countPersonalConsecutiveCourtStreak from sessionMatchModel */
export function countPersonalConsecutiveInteractionStreak(player1Id, player2Id, matchHistory) {
  return countPersonalConsecutiveCourtStreak(player1Id, player2Id, matchHistory);
}

/**
 * Soft penalty when proposed players largely repeat each player's personal last doubles roster.
 * Uses per-player last game (not session-last court). Never hard-blocks.
 * @param {string[]} allPlayers - 4 proposed player ids
 * @param {Object} sessionState
 * @param {Object} config - NORMALIZED_SCORING_CONFIG.interactions
 * @returns {{ penalty: number, affected3: number, affected4: number }}
 */
export function calculatePersonalPodOverlapPenalty(allPlayers, sessionState, config) {
  if (!sessionState?.matchHistory?.length || !allPlayers?.length) {
    return { penalty: 0, affected3: 0, affected4: 0 };
  }

  let penalty = 0;
  let affected3 = 0;
  let affected4 = 0;
  const overlap3 = config.overlap3Penalty ?? 25;
  const overlap4 = config.overlap4Penalty ?? 50;

  for (const playerId of allPlayers) {
    const personalGames = getPersonalGamesForPlayer(sessionState.matchHistory, playerId);
    const lastGameForPlayer = personalGames.length > 0 ? personalGames[personalGames.length - 1] : null;
    if (!lastGameForPlayer) continue;

    const lastFour = lastGameForPlayer.allPlayerIds;
    const overlapCount = allPlayers.filter((id) => lastFour.has(String(id))).length;

    if (overlapCount >= 4) {
      affected4 += 1;
      penalty += overlap4;
    } else if (overlapCount >= 3) {
      affected3 += 1;
      penalty += overlap3;
    }
  }

  const maxPenalty = config.podOverlapMaxPenalty ?? 90;
  penalty = Math.min(penalty, maxPenalty);

  return { penalty, affected3, affected4 };
}

export function calculateInteractionScore(
  team1,
  team2,
  sessionState,
  partnerships = [],
  hardBlocks = null,
  scheduledCombinations = [],
) {
  const config = NORMALIZED_SCORING_CONFIG.interactions;
  const allPlayers = [...team1, ...team2];
  
  // Get consecutive threshold (can be overridden by relaxation). undefined = use config default; null = Level 6 relaxed (no block).
  const rawConsecutiveThreshold = hardBlocks?.consecutiveThreshold;
  const consecutiveThreshold = rawConsecutiveThreshold === undefined
    ? (MATCHMAKING_CONFIG.consecutiveInteractionBlock?.threshold ?? 2)
    : rawConsecutiveThreshold;
  
  // Build partnership map for exemptions
  const partnershipMap = new Map();
  partnerships.forEach(p => {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    partnershipMap.set(p1, p2);
    partnershipMap.set(p2, p1);
  });
  
  let totalInteractionWeight = 0;
  let maxPairInteractionWeight = 0;
  let consecutiveCount = 0;
  let densityPenalty = 0;
  
  // Check all player pairs
  for (let i = 0; i < allPlayers.length; i++) {
    for (let j = i + 1; j < allPlayers.length; j++) {
      const player1Id = allPlayers[i];
      const player2Id = allPlayers[j];
      
      // Skip if partnership (they're supposed to play together)
      if (partnershipMap.get(player1Id) === player2Id) {
        continue;
      }

      if (wouldExceedInteractionDensityBlock(player1Id, player2Id, sessionState, hardBlocks)) {
        densityPenalty += 80;
        debugLog('📉 Session interaction density penalty (strict)', {
          player1Id: player1Id.slice(0, 8),
          player2Id: player2Id.slice(0, 8),
        });
      }
      
      // Calculate recency-weighted interactions (soft scoring)
      const interactions = getRecentInteractions(player1Id, player2Id, sessionState, config.recentGameWindow);
      const weightedInteractions = calculateRecencyWeight(interactions, config.decayFactor);
      
      totalInteractionWeight += weightedInteractions;
      maxPairInteractionWeight = Math.max(maxPairInteractionWeight, weightedInteractions);
      
      // Per-player consecutive streak on full history (hard block uses this below)
      const personalConsecutive = countPersonalConsecutiveCourtStreak(
        player1Id,
        player2Id,
        sessionState.matchHistory,
      );
      consecutiveCount = Math.max(consecutiveCount, personalConsecutive);
    }
  }
  
  // Calculate base score (0-100) based on total interaction weight
  const pairCount = Math.max(1, (allPlayers.length * (allPlayers.length - 1)) / 2);
  const avgInteractionWeight = totalInteractionWeight / pairCount;
  
  let baseScore = 100;
  if (avgInteractionWeight > config.low) {
    if (avgInteractionWeight <= config.moderate) {
      baseScore = 100 - ((avgInteractionWeight - config.low) / (config.moderate - config.low)) * 25;
    } else if (avgInteractionWeight <= config.high) {
      baseScore = 75 - ((avgInteractionWeight - config.moderate) / (config.high - config.moderate)) * 25;
    } else if (avgInteractionWeight <= config.veryHigh) {
      baseScore = 50 - ((avgInteractionWeight - config.high) / (config.veryHigh - config.high)) * 25;
    } else {
      baseScore = Math.max(0, 25 - ((avgInteractionWeight - config.veryHigh) * 50));
    }
  }

  const noveltyMaxBonus = config.sessionPlayerNoveltyMaxBonus ?? 0;
  if (noveltyMaxBonus > 0) {
    const avgNovelFraction = calculateAverageSessionPlayerNoveltyFraction(
      allPlayers,
      sessionState,
      partnershipMap,
    );
    const noveltyBonus = avgNovelFraction * noveltyMaxBonus;
    if (noveltyBonus > 0) {
      baseScore = Math.min(100, baseScore + noveltyBonus);
      debugLog('✨ Session player novelty bonus (interactions)', {
        avgNovelFraction: Number(avgNovelFraction.toFixed(3)),
        noveltyBonus: Number(noveltyBonus.toFixed(2)),
      });
    }
  }

  // Apply consecutive interaction penalties or hard block
  if (consecutiveCount >= 2) {
    baseScore -= config.consecutive2Penalty;
  }
  if (consecutiveCount >= 3) {
    baseScore -= config.consecutive3PlusPenalty;
  }

  baseScore -= densityPenalty;

  const podOverlap = calculatePersonalPodOverlapPenalty(allPlayers, sessionState, config);
  if (podOverlap.penalty > 0) {
    baseScore -= podOverlap.penalty;
    debugLog('📉 Personal pod overlap penalty (interactions)', {
      penalty: podOverlap.penalty,
      affected3: podOverlap.affected3,
      affected4: podOverlap.affected4,
      consecutiveCount,
    });
  }

  if (
    allPlayers.length === 4
    && checkExactRepeatGame(team1, team2, sessionState, null, scheduledCombinations)
  ) {
    baseScore -= config.exactIdenticalTeamPenalty ?? 35;
    debugLog('📉 Exact identical team soft penalty (interactions)', {
      penalty: config.exactIdenticalTeamPenalty ?? 35,
    });
  }

  // Check for hard block if enabled. consecutiveThreshold null = fully relaxed (no block).
  // Semantics: block when personal streak >= threshold (threshold 2 → block 3rd consecutive personal game).
  if (MATCHMAKING_CONFIG.consecutiveInteractionBlock?.enabled && consecutiveThreshold != null) {
    if (consecutiveCount >= consecutiveThreshold) {
      debugLog('❌ Hard block: Personal consecutive interaction streak exceeded', {
        consecutiveCount,
        threshold: consecutiveThreshold,
      });
      return -Infinity;
    }
  }
  
  return Math.max(0, baseScore);
}

// WeakMap cache: invalidated automatically when sessionState is a new object reference.
// This avoids repeated O(H) history scans within a single evaluation pass.
const _gameTypeCountsCache = new WeakMap();

/**
 * Count session games by type (mens doubles, mixed doubles, womens doubles) from matchHistory.
 * Cached per sessionState object reference — safe to call many times in one evaluation.
 * @param {Object} sessionState - Session state with matchHistory (each game: team1, team2 or teams, player IDs)
 * @param {Object} playersById - Map of player id -> { gender, ... }
 * @returns {{ mixed: number, mens: number, womens: number }}
 */
export function getSessionGameTypeCounts(sessionState, playersById, playerWaitMetrics = null) {
  const counts = { mixed: 0, mens: 0, womens: 0 };
  if (!sessionState?.matchHistory?.length || !playersById) return counts;

  const pwm = playerWaitMetrics ?? sessionState?.playerWaitMetrics ?? {};

  if (_gameTypeCountsCache.has(sessionState)) {
    return _gameTypeCountsCache.get(sessionState);
  }

  for (const game of sessionState.matchHistory) {
    let team1 = game.team1;
    let team2 = game.team2;
    if ((!Array.isArray(team1) || !Array.isArray(team2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
      [team1, team2] = game.teams;
    }
    if (!Array.isArray(team1) || !Array.isArray(team2)) continue;

    if (team1.length === 1 && team2.length === 1) {
      const g1 = playersById[team1[0]]?.gender;
      const g2 = playersById[team2[0]]?.gender;
      if (g1 === 'male' && g2 === 'male') counts.mens += 1;
      else if (g1 === 'female' && g2 === 'female') counts.womens += 1;
      continue;
    }

    if (team1.length !== 2 || team2.length !== 2) continue;

    const allIds = [...team1, ...team2];
    if (isSessionMensDoublesEloExemptLineup(team1, team2, pwm, playersById)) {
      counts.mens += 1;
      continue;
    }

    const genders = allIds.map(id => playersById[id]?.gender).filter(Boolean);
    if (genders.length !== 4) continue;

    const team1Genders = team1.map(id => playersById[id]?.gender).filter(Boolean);
    const team2Genders = team2.map(id => playersById[id]?.gender).filter(Boolean);
    const t1Mixed = team1Genders.includes('male') && team1Genders.includes('female');
    const t2Mixed = team2Genders.includes('male') && team2Genders.includes('female');
    const allMale = genders.every(g => g === 'male');
    const allFemale = genders.every(g => g === 'female');

    if (t1Mixed && t2Mixed) counts.mixed += 1;
    else if (allMale) counts.mens += 1;
    else if (allFemale) counts.womens += 1;
  }
  _gameTypeCountsCache.set(sessionState, counts);
  return counts;
}

/**
 * Get proposed game type: 'mixed' | 'mens' | 'womens' or null if unbalanced.
 * @param {Object} [playerWaitMetrics] - Session mens eligibility flags
 * @param {Object} [playersById] - Player lookup for session mens detection
 */
export function getProposedGameType(team1Players, team2Players, playerWaitMetrics = null, playersById = null) {
  const _pb = playersById || Object.fromEntries(
    [...team1Players, ...team2Players].filter((p) => p?.id).map((p) => [p.id, p]),
  );
  const _pwm = playerWaitMetrics || {};
  const t1Ids = team1Players.map((p) => p?.id).filter(Boolean);
  const t2Ids = team2Players.map((p) => p?.id).filter(Boolean);
  if (
    t1Ids.length === 2 &&
    t2Ids.length === 2 &&
    isSessionMensDoublesEloExemptLineup(t1Ids, t2Ids, _pwm, _pb)
  ) {
    return 'mens';
  }

  const isMixed = (team) => team.some(p => p.gender === 'male') && team.some(p => p.gender === 'female');
  const isAllGender = (team, g) => team.every(p => p.gender === g);
  const t1Mixed = isMixed(team1Players);
  const t2Mixed = isMixed(team2Players);
  if (t1Mixed && t2Mixed) return 'mixed';
  if (isAllGender(team1Players, 'male') && isAllGender(team2Players, 'male')) return 'mens';
  if (isAllGender(team1Players, 'female') && isAllGender(team2Players, 'female')) return 'womens';
  return null;
}

/**
 * Calculate game type score based on balance
 * Returns 0-100 or -Infinity for hard blocks
 * @param {Array} team1Players - Team 1 player objects
 * @param {Array} team2Players - Team 2 player objects
 * @param {string} gameTypeRestriction - Game type restriction setting
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 * @param {Object} sessionState - Optional; used for under-represented game type bonus (game-level)
 * @param {Object} playersById - Optional; used with sessionState to count session game types
 * @param {Object} playerWaitMetrics - Optional; used for mens-doubles-eligible effective gender
 */
export function calculateGameTypeScore(team1Players, team2Players, gameTypeRestriction = 'all_types', hardBlocks = null, sessionState = null, playersById = null, playerWaitMetrics = null) {
  const config = NORMALIZED_SCORING_CONFIG.gameType;
  
  // Check if game type enforcement is relaxed
  const enforceGameType = hardBlocks?.enforceGameType !== false;

  const isSinglesGame = team1Players.length === 1 && team2Players.length === 1;
  if (isSinglesGame) {
    const policy = getSinglesPairingPolicy(gameTypeRestriction, MATCH_FORMAT_SINGLES);
    if (!pairAllowedByPolicy(team1Players[0]?.gender, team2Players[0]?.gender, policy)) {
      return enforceGameType ? -Infinity : config.unbalanced;
    }
    if (enforceGameType && gameTypeRestriction === 'mens_only') {
      if (!(team1Players[0]?.gender === 'male' && team2Players[0]?.gender === 'male')) return -Infinity;
    }
    if (enforceGameType && gameTypeRestriction === 'ladies_only') {
      if (!(team1Players[0]?.gender === 'female' && team2Players[0]?.gender === 'female')) return -Infinity;
    }
    if (enforceGameType && gameTypeRestriction === 'mixed_only') {
      let s = config.balanced;
      s = applyGameTypeVarietyBonus(s, team1Players, team2Players, false, false, config, sessionState, playersById);
      return applyUnderRepresentedBonus(s, team1Players, team2Players, sessionState, playersById, config);
    }
  }
  
  const isMixed = (team) => {
    const genders = team.map((p) => (p?.gender || '').toLowerCase()).filter(Boolean);
    return genders.includes('male') && genders.includes('female');
  };
  
  const isAllGender = (team, gender) => {
    const want = (gender || '').toLowerCase();
    const players = team.filter(Boolean);
    if (!want || players.length === 0) return false;
    return players.every((p) => (p.gender || '').toLowerCase() === want);
  };
  
  const team1Mixed = isMixed(team1Players);
  const team2Mixed = isMixed(team2Players);

  const _pwm = playerWaitMetrics || sessionState?.playerWaitMetrics || {};
  const _pb = playersById || Object.fromEntries(
    [...team1Players, ...team2Players].filter((p) => p?.id).map((p) => [p.id, p]),
  );
  const t1Ids = team1Players.map((p) => p?.id).filter(Boolean);
  const t2Ids = team2Players.map((p) => p?.id).filter(Boolean);
  
  let score;
  
  // Skip hard blocks if game type enforcement is relaxed
  if (!enforceGameType) {
    score = team1Mixed === team2Mixed ? config.balanced : config.suboptimal;
    score = applyGameTypeVarietyBonus(score, team1Players, team2Players, team1Mixed, team2Mixed, config, sessionState, playersById);
    return applyUnderRepresentedBonus(score, team1Players, team2Players, sessionState, playersById, config);
  }
  
  // Check for hard blocks based on game type restriction (only if enforced)
  if (gameTypeRestriction === 'balanced_only') {
    // Eligible-female players can form effective-all-male teams under balanced_only.
    const t1EffAllMale = t1Ids.length > 0 && t1Ids.every(id => {
      const p = _pb[id] || team1Players.find(pl => pl?.id === id);
      const g = (p?.gender || '').toLowerCase();
      return g === 'male' || (g === 'female' && _pwm[id]?.mensDoublesEligible === true);
    });
    const t2EffAllMale = t2Ids.length > 0 && t2Ids.every(id => {
      const p = _pb[id] || team2Players.find(pl => pl?.id === id);
      const g = (p?.gender || '').toLowerCase();
      return g === 'male' || (g === 'female' && _pwm[id]?.mensDoublesEligible === true);
    });

    // Both effectively-all-male → valid
    if (t1EffAllMale && t2EffAllMale) {
      // fall through to scoring below
    } else if (team1Mixed && team2Mixed) {
      // Both mixed → valid, fall through
    } else if (isAllGender(team1Players, 'female') && isAllGender(team2Players, 'female')) {
      // Both physically all-female → valid womens doubles
    } else {
      return -Infinity;
    }
  }
  
  if (gameTypeRestriction === 'mixed_only') {
    if (!team1Mixed || !team2Mixed) {
      return -Infinity;
    }
  }

  // Host-endorsed session mens: 3M + 1 eligible F, scored as balanced mens
  if (
    t1Ids.length === 2 &&
    t2Ids.length === 2 &&
    isSessionMensDoublesEloExemptLineup(t1Ids, t2Ids, _pwm, _pb)
  ) {
    score = applyGameTypeVarietyBonus(
      config.balanced,
      team1Players,
      team2Players,
      false,
      false,
      config,
      sessionState,
      playersById,
    );
    return applyUnderRepresentedBonus(score, team1Players, team2Players, sessionState, playersById, config);
  }
  
  // Score based on balance
  // Balanced matchups (both same type)
  if (team1Mixed === team2Mixed) {
    score = applyGameTypeVarietyBonus(config.balanced, team1Players, team2Players, team1Mixed, team2Mixed, config, sessionState, playersById);
    return applyUnderRepresentedBonus(score, team1Players, team2Players, sessionState, playersById, config);
  }
  
  // Mens vs mixed or womens vs mixed (suboptimal)
  if ((isAllGender(team1Players, 'male') || isAllGender(team1Players, 'female')) &&
      (team1Mixed !== team2Mixed)) {
    return config.suboptimal;
  }
  
  // Mens vs womens (very unbalanced)
  if ((isAllGender(team1Players, 'male') && isAllGender(team2Players, 'female')) ||
      (isAllGender(team1Players, 'female') && isAllGender(team2Players, 'male'))) {
    return config.unbalanced;
  }
  
  score = config.suboptimal;
  return applyUnderRepresentedBonus(score, team1Players, team2Players, sessionState, playersById, config);
}

/**
 * If the proposed game is mens/mixed/womens and that type is under-represented, add a bonus that scales with
 * "games behind" (how many fewer games this type has than the second-lowest). Part of the game-type dimension
 * so hosts can prioritise variety by increasing the gameType weight in scoring config.
 */
function applyUnderRepresentedBonus(score, team1Players, team2Players, sessionState, playersById, config) {
  if (score === -Infinity || score == null) return score;
  const perGame = config.underRepresentedBonusPerGameBehind ?? 0;
  const cap = config.underRepresentedBonusCap ?? 28;
  if (!perGame || !sessionState || !playersById) return score;

  const proposedType = getProposedGameType(
    team1Players,
    team2Players,
    sessionState?.playerWaitMetrics || {},
    playersById,
  );
  if (proposedType == null) return score; // Unbalanced game, no bonus

  const counts = getSessionGameTypeCounts(sessionState, playersById, sessionState?.playerWaitMetrics);
  const countForType = counts[proposedType];
  const sorted = [counts.mixed, counts.mens, counts.womens].sort((a, b) => a - b);
  const minCount = sorted[0];
  const secondLowest = sorted[1];
  if (countForType !== minCount) return score; // Not (or not tied for) minimum

  const totalGames = counts.mixed + counts.mens + counts.womens;
  const gamesBehind = totalGames === 0 ? 1 : Math.max(0, secondLowest - countForType);
  const bonus = Math.min(cap, perGame * gamesBehind);
  if (bonus <= 0) return score;

  const newScore = Math.min(100, score + bonus);
  debugLog('📈 Under-represented game type bonus (games behind)', { proposedType, counts, gamesBehind, bonus, score, newScore });
  return newScore;
}

// Per-player womens games cache: WeakMap<sessionState, Map<playerId, count>>.
// Avoids repeated full history scans for the same player within one evaluation pass.
const _playerWomensGamesCache = new WeakMap();

/**
 * Count women's doubles games played by a specific player from match history.
 * Cached per (sessionState, playerId) pair — safe to call many times in one evaluation.
 * Used to give female players accurate variety scores for women's doubles vs mixed.
 */
function getPlayerWomensGamesFromHistory(playerId, sessionState, playersById) {
  if (!sessionState?.matchHistory || !playersById) return 0;

  let playerCache = _playerWomensGamesCache.get(sessionState);
  if (!playerCache) {
    playerCache = new Map();
    _playerWomensGamesCache.set(sessionState, playerCache);
  }
  if (playerCache.has(playerId)) {
    return playerCache.get(playerId);
  }

  let count = 0;
  for (const game of sessionState.matchHistory) {
    let team1 = game.team1;
    let team2 = game.team2;
    if ((!Array.isArray(team1) || !Array.isArray(team2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
      [team1, team2] = game.teams;
    }
    if (!Array.isArray(team1) || !Array.isArray(team2)) continue;
    if (team1.length !== 2 || team2.length !== 2) continue;
    const allIds = [...team1, ...team2];
    if (!allIds.includes(playerId)) continue;
    const genders = allIds.map(id => playersById[id]?.gender);
    if (genders.every(g => g === 'female')) count += 1;
  }
  playerCache.set(playerId, count);
  return count;
}

function applyGameTypeVarietyBonus(baseScore, team1Players, team2Players, team1Mixed, team2Mixed, config, sessionState = null, playersById = null) {
  if (team1Mixed !== team2Mixed) {
    return baseScore;
  }

  const allPlayers = [...team1Players, ...team2Players];

  // Distinguish womens doubles from mens doubles when both teams are levels (non-mixed)
  const isWomens = !team1Mixed &&
    allPlayers.every(p => (p?.gender || '').toLowerCase() === 'female');
  const proposedGameType = team1Mixed ? 'mixed' : (isWomens ? 'womens' : 'mens');

  const varietyScores = allPlayers.map(player => {
    const mixedGames = player?.sessionMixedGames ?? 0;
    let targetCount;
    let otherCount;

    if (proposedGameType === 'mixed') {
      targetCount = mixedGames;
      otherCount = player?.sessionLevelsGames ?? 0;
    } else if (proposedGameType === 'womens') {
      // For women's doubles, compare a player's womens games vs their mixed games.
      // Derive from match history when available; fall back to sessionLevelsGames.
      const womensGames = (sessionState && playersById)
        ? getPlayerWomensGamesFromHistory(player?.id, sessionState, playersById)
        : (player?.sessionLevelsGames ?? 0);
      targetCount = womensGames;
      otherCount = mixedGames;
    } else {
      // mens doubles
      targetCount = player?.sessionLevelsGames ?? 0;
      otherCount = mixedGames;
    }

    const total = targetCount + otherCount;
    if (total === 0) return 50;
    const balance = (otherCount - targetCount) / total; // -1..1
    const normalized = Math.max(-1, Math.min(1, balance));
    return 50 + normalized * 50;
  });

  const varietyScore = varietyScores.reduce((sum, score) => sum + score, 0) / varietyScores.length;
  if (!Number.isFinite(varietyScore)) {
    return baseScore;
  }

  const varietyWeight = config.varietyWeight ?? 0;
  return (baseScore * (1 - varietyWeight)) + (varietyScore * varietyWeight);
}

/**
 * Calculate category score based on skill tier gaps
 * Returns 0-100 or -Infinity for hard blocks
 * @param {Object} [playerWaitMetrics=null] - Used to determine effective game type key for eligible females
 */
export function calculateCategoryScore(team1Players, team2Players, playerCategories = null, hardBlocks = null, playerWaitMetrics = null) {
  const config = NORMALIZED_SCORING_CONFIG.categories;
  
  if (!playerCategories) {
    return 100; // No categories defined, perfect score
  }
  
  // Get category gap threshold. undefined = use default 3 (block 4+). Explicit null = relaxed (no block).
  const categoryGapThreshold = hardBlocks?.categoryGap;
  const effectiveGapThreshold = categoryGapThreshold === undefined ? 3 : categoryGapThreshold;
  
  const gameTypeKey = getGameTypeKey(team1Players, team2Players, playerWaitMetrics || {});
  const allPlayers = [...team1Players, ...team2Players];
  
  // Get category levels for all players using the resolver (handles all three playerCategories shapes)
  const levelMap = { 'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5 };
  const categoryLevels = allPlayers.map(p => {
    const category = resolveCategoryLabel(p, playerCategories, gameTypeKey);
    if (!category) return null;
    return levelMap[category] || null;
  });
  
  // If any player has no category, return neutral score
  if (categoryLevels.some(l => l === null)) {
    return 75;
  }
  
  // Calculate gaps
  const minLevel = Math.min(...categoryLevels);
  const maxLevel = Math.max(...categoryLevels);
  const maxGap = maxLevel - minLevel;
  
  // Hard block when gap reaches the threshold (gap >= threshold, consistent with legacy path)
  if (effectiveGapThreshold != null && maxGap >= effectiveGapThreshold) {
    return -Infinity;
  }
  
  // Score based on maximum gap
  if (maxGap <= config.perfect) {
    return 100; // All same category
  }
  
  if (maxGap <= config.good) {
    return 75; // Max 1 category gap
  }
  
  if (maxGap <= config.acceptable) {
    return 50; // Max 2 category gap
  }
  
  // Max 3 category gap (poor but allowed)
  return 25;
}

/**
 * Check if partnerships would be violated (partners on opposite teams)
 * Returns -Infinity if violation found
 * 
 * CRITICAL: This check is NEVER relaxable - partnerships are sacred
 * 
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Array} partnerships - Active partnerships
 * @param {{ poolPlayerIds?: string[]|Set<string> }} [options]
 * @returns {number} 100 if no violation, -Infinity if violation
 */
export function checkPartnershipViolation(team1, team2, partnerships, options = {}) {
  if (!partnerships || partnerships.length === 0) {
    return 100; // No partnerships to violate
  }

  const poolSet = options.poolPlayerIds
    ? new Set(options.poolPlayerIds)
    : null;
  
  for (const partnership of partnerships) {
    const p1 = partnership.player1Id || partnership.player1_id;
    const p2 = partnership.player2Id || partnership.player2_id;
    
    if (!p1 || !p2) continue;
    
    // Check if both partners are in this game
    const p1Team = team1.includes(p1) ? 1 : team2.includes(p1) ? 2 : 0;
    const p2Team = team1.includes(p2) ? 1 : team2.includes(p2) ? 2 : 0;
    
    // If both in game but on different teams = VIOLATION
    if (p1Team > 0 && p2Team > 0 && p1Team !== p2Team) {
      debugLog('🚫 PARTNERSHIP VIOLATION - HARD BLOCK (NEVER RELAXABLE):', {
        partnership: `${p1.slice(0, 8)} + ${p2.slice(0, 8)}`,
        p1Team,
        p2Team,
        team1: team1.map(id => id.slice(0, 8)),
        team2: team2.map(id => id.slice(0, 8)),
      });
      return -Infinity; // ALWAYS block, NEVER relax
    }

    // Lone partner: one in game, one out — block when the waiting partner is still
    // in the matchmaking pool (they could have been placed together).
    const p1InGame = p1Team > 0;
    const p2InGame = p2Team > 0;
    if (p1InGame !== p2InGame && poolSet?.has(p1) && poolSet?.has(p2)) {
      debugLog('🚫 PARTNERSHIP VIOLATION - lone partner while partner still in pool:', {
        partnership: `${p1.slice(0, 8)} + ${p2.slice(0, 8)}`,
        p1InGame,
        p2InGame,
      });
      return -Infinity;
    }
  }
  
  return 100; // No violation
}

/**
 * Check if any player would reach min+2 games without making session fairer
 * Returns -Infinity if blocked (unless relaxed via hardBlocks)
 * 
 * This prevents runaway game counts unless the game would improve fairness
 * Can be relaxed at higher relaxation levels to resolve deadlocks
 * 
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} playersById - Player lookup map
 * @param {number} gamesMin - Minimum games in session (pool-local)
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 * @param {Object} [options]
 * @param {string[]} [options.poolPlayerIds] - Eligible waitlist pool for substitute check
 * @param {number} [options.sessionWideMinGames] - Session min across all active players (courts + waitlist)
 * @returns {number} 100 if allowed, -Infinity if blocked
 */
export function checkMinPlus2FairnessGuard(
  team1,
  team2,
  playersById,
  gamesMin,
  hardBlocks = null,
  options = null,
) {
  // If relaxed at high level, skip check
  if (hardBlocks?.minPlus2Guard === false) {
    debugLog('✅ Min+2 guard RELAXED via hardBlocks');
    return 100; // No block
  }
  
  if (gamesMin === null || gamesMin === undefined) {
    return 100; // No min to check against
  }

  const guardMin =
    options?.sessionWideMinGames != null
      ? Math.min(gamesMin, options.sessionWideMinGames)
      : gamesMin;
  
  const allPlayerIds = [...team1, ...team2];
  
  // Check if any player would reach min+2
  let wouldBlock = false;
  const blockingPlayers = [];
  
  for (const id of allPlayerIds) {
    const player = playersById[id];
    const currentGames = player?.effectiveGamesPlayed || player?.gamesPlayedThisSession || 0;
    
    // Already at or past session-wide min+2
    if (currentGames >= guardMin + 2) {
      wouldBlock = true;
      blockingPlayers.push(id);
    }
  }
  
  if (!wouldBlock) {
    return 100; // No issue
  }
  
  // Check if game would make session fairer
  let wouldBeFair = wouldBeFairAfterGame(team1, team2, playersById, guardMin);

  const poolPlayerIds = options?.poolPlayerIds;
  if (Array.isArray(poolPlayerIds) && poolPlayerIds.length > 0 && blockingPlayers.length > 0) {
    const blockerHasSubstitute = (id) =>
      hasLowerGamesSubstituteOutsideQuartet(
        id,
        team1,
        team2,
        poolPlayerIds,
        playersById,
        gamesMin,
        {
          sessionState: options?.sessionState,
          playerWaitMetrics: options?.playerWaitMetrics,
        },
      );

    if (wouldBeFair && blockingPlayers.some(blockerHasSubstitute)) {
      wouldBeFair = false;
    } else if (!wouldBeFair && blockingPlayers.every((id) => !blockerHasSubstitute(id))) {
      // All min+2 players are required — no lower-game waitlist alternative.
      wouldBeFair = true;
    }
  }
  
  if (!wouldBeFair) {
    debugLog('🚫 MIN+2 FAIRNESS GUARD - HARD BLOCK:', {
      blockingPlayers: blockingPlayers.map(id => playersById[id]?.name || id.slice(0, 8)),
      team1: team1.map(id => playersById[id]?.name || id.slice(0, 8)),
      team2: team2.map(id => playersById[id]?.name || id.slice(0, 8)),
      gamesMin,
      guardMin,
      wouldBeFair: false,
    });
    return -Infinity; // Hard block
  }
  
  // Would be fair - allow with note
  debugLog('✅ Min+2 override: Game would improve fairness', {
    blockingPlayers: blockingPlayers.map(id => playersById[id]?.name || id.slice(0, 8)),
  });
  return 100;
}

function playerEffectiveGames(playersById, id) {
  return playersById[id]?.effectiveGamesPlayed ?? playersById[id]?.gamesPlayedThisSession ?? 0;
}

/** True when a waitlist player with fewer games could replace a min+2 blocker. */
function hasLowerGamesSubstituteOutsideQuartet(
  blockingPlayerId,
  team1,
  team2,
  poolPlayerIds,
  playersById,
  poolGamesMin,
  options = null,
) {
  const quartet = new Set([...team1, ...team2]);
  const blockingGames = playerEffectiveGames(playersById, blockingPlayerId);
  const playerWaitMetrics = options?.playerWaitMetrics || {};
  const sessionState = options?.sessionState || null;
  let tierMedians = null;
  let blockingPriority = null;

  if (isSessionDebtModelEnabled() && sessionState) {
    tierMedians = computeTierMedians(poolPlayerIds, playersById, playerWaitMetrics);
    blockingPriority = computePlayerDebts(
      blockingPlayerId,
      playersById,
      playerWaitMetrics,
      tierMedians,
      poolGamesMin,
      sessionState,
    ).priority;
  }

  for (const id of poolPlayerIds) {
    if (quartet.has(id)) continue;
    const g = playerEffectiveGames(playersById, id);
    if (g < blockingGames && g <= poolGamesMin + 1) {
      return true;
    }
    if (
      tierMedians
      && blockingPriority != null
      && g === blockingGames
    ) {
      const subPriority = computePlayerDebts(
        id,
        playersById,
        playerWaitMetrics,
        tierMedians,
        poolGamesMin,
        sessionState,
      ).priority;
      if (subPriority > blockingPriority + 0.5) {
        return true;
      }
    }
  }
  return false;
}

/**
 * True when the match includes a player at session min who would still be
 * catching up (not leap-frogging) after one game — e.g. lone G:0 with G:2 pool.
 */
function isSessionMinCatchUpGame(team1, team2, playersById, gamesMin) {
  const allPlayers = [...team1, ...team2];
  let maxBefore = -Infinity;
  for (const id of allPlayers) {
    const currentGames = playersById[id]?.effectiveGamesPlayed ?? playersById[id]?.gamesPlayedThisSession ?? 0;
    maxBefore = Math.max(maxBefore, currentGames);
  }
  const maxAfter = maxBefore + 1;

  return allPlayers.some((id) => {
    const currentGames = playersById[id]?.effectiveGamesPlayed ?? playersById[id]?.gamesPlayedThisSession ?? 0;
    if (currentGames !== gamesMin) return false;
    return currentGames + 1 < maxAfter;
  });
}

/**
 * Helper: Check if game would make session fairer
 * @returns {boolean} True if the game would improve fairness
 */
function wouldBeFairAfterGame(team1, team2, playersById, gamesMin) {
  const allPlayers = [...team1, ...team2];

  const maxGamesAfter = Math.max(...allPlayers.map(id => {
    const player = playersById[id];
    const currentGames = player?.effectiveGamesPlayed || player?.gamesPlayedThisSession || 0;
    return currentGames + 1;
  }));

  if (maxGamesAfter <= gamesMin + 2) {
    return true;
  }

  if (isSessionMinCatchUpGame(team1, team2, playersById, gamesMin)) {
    const hasMinPlusTwo = allPlayers.some((id) => {
      const g = playersById[id]?.effectiveGamesPlayed ?? playersById[id]?.gamesPlayedThisSession ?? 0;
      return g >= gamesMin + 2;
    });
    const hasMinPlusOne = allPlayers.some((id) => {
      const g = playersById[id]?.effectiveGamesPlayed ?? playersById[id]?.gamesPlayedThisSession ?? 0;
      return g === gamesMin + 1;
    });
    // Min+2 cannot piggyback on session-min catch-up when min+1 players are already in the game.
    if (hasMinPlusTwo && hasMinPlusOne) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * Check if same pair just played together on same team in their most recent game (per-player perspective).
 * Returns -Infinity if blocked (unless relaxed via hardBlocks).
 *
 * Uses the most recent game that involved BOTH players (not the session's last game), so e.g.
 * session games 9 and 16 can be that pair's games 2 and 3 — we block game 16 if they were together in game 9.
 *
 * Partnerships are exempt from this check.
 *
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} sessionState - Session state with matchHistory
 * @param {Array} partnerships - Active partnerships
 * @param {Object} playersById - Player lookup map
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 * @returns {number} 100 if allowed, -Infinity if blocked
 */
export function checkImmediateRepeatTeam(team1, team2, sessionState, partnerships, playersById, hardBlocks = null, scheduledCombinations = []) {
  // Can be relaxed at medium levels
  if (hardBlocks?.allowImmediateRepeat === true) {
    return 100; // Relaxed
  }

  const isScheduled = scheduledCombinations?.some(scheduledCombination =>
    team1.every(id => scheduledCombination.includes(id)) &&
    team2.every(id => scheduledCombination.includes(id))
  );
  if (isScheduled) {
    return 100; // Scheduled games are exempt
  }

  if (!sessionState?.matchHistory || sessionState.matchHistory.length === 0) {
    return 100; // No history
  }

  // Build partnership map for exemptions
  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    for (const p of partnerships) {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    }
  }

  // For each proposed team (pair), find the most recent game that involved BOTH players (per-player perspective)
  const checkPair = (currentTeam) => {
    if (!currentTeam || currentTeam.length !== 2) return 100;
    const [p1, p2] = currentTeam;
    if (partnershipMap.has(p1) && partnershipMap.get(p1) === p2) return 100; // Partnership exempt

    // Walk backwards: most recent game first (session order)
    for (let i = sessionState.matchHistory.length - 1; i >= 0; i--) {
      const game = sessionState.matchHistory[i];
      let gameTeam1 = game.team1;
      let gameTeam2 = game.team2;
      if ((!Array.isArray(gameTeam1) || !Array.isArray(gameTeam2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
        [gameTeam1, gameTeam2] = game.teams;
      }
      if (!Array.isArray(gameTeam1) || !Array.isArray(gameTeam2)) continue;

      const p1InGame = gameTeam1.includes(p1) || gameTeam2.includes(p1);
      const p2InGame = gameTeam1.includes(p2) || gameTeam2.includes(p2);
      if (!p1InGame || !p2InGame) continue; // First game that contains both players

      const sameTeamInGame =
        (gameTeam1.includes(p1) && gameTeam1.includes(p2)) ||
        (gameTeam2.includes(p1) && gameTeam2.includes(p2));
      if (sameTeamInGame) {
        debugLog('🚫 IMMEDIATE REPEAT TEAM - HARD BLOCK:', {
          team: currentTeam.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
          context: 'Played together in their most recent game together (per-player perspective)',
        });
        return -Infinity; // Block: they were on same team in the most recent game that involved both
      }
      return 100; // They were in the same game but on opposite teams — no block
    }
    return 100; // No shared game found
  };

  const team1Result = checkPair(team1);
  if (team1Result === -Infinity) return -Infinity;
  const team2Result = checkPair(team2);
  if (team2Result === -Infinity) return -Infinity;
  return 100;
}

/**
 * Check if same pair played together within the last N games (per-player).
 * Returns -Infinity if blocked (unless relaxed via hardBlocks).
 *
 * Window is per player: we block if we find a past game where this pair played together
 * and either player has played <= windowSize games since that game. So the pair can team
 * again only after BOTH have played more than windowSize games since (e.g. windowSize=6
 * means both must have 7+ games since last together). Partnerships are exempt.
 *
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} sessionState - Session state with matchHistory
 * @param {Array} partnerships - Active partnerships
 * @param {Object} playersById - Player lookup map
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 * @returns {number} 100 if allowed, -Infinity if blocked
 */
export function checkRecentRepeatTeam(team1, team2, sessionState, partnerships, playersById, hardBlocks = null, scheduledCombinations = []) {
  if (hardBlocks?.allowRecentRepeat === true) {
    return 100; // Relaxed
  }

  const isScheduled = scheduledCombinations?.some(scheduledCombination =>
    team1.every(id => scheduledCombination.includes(id)) &&
    team2.every(id => scheduledCombination.includes(id))
  );
  if (isScheduled) {
    return 100; // Scheduled games are exempt
  }

  const windowSize = hardBlocks?.repeatTeamWindow ?? MATCHMAKING_CONFIG.repeatTeamWindow ?? 6;
  if (!sessionState?.matchHistory || sessionState.matchHistory.length === 0) {
    return 100;
  }

  // Build partnership map for exemptions
  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    for (const p of partnerships) {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    }
  }

  // Helper to check if a team matches a previous team (order-independent)
  const teamsMatch = (currentTeam, pastTeam) => {
    if (!currentTeam || !pastTeam || currentTeam.length !== 2 || pastTeam.length !== 2) {
      return false;
    }
    const currentSet = new Set(currentTeam);
    const pastSet = new Set(pastTeam);
    return currentSet.size === pastSet.size && [...currentSet].every(id => pastSet.has(id));
  };

  const hasRecentRepeatForTeam = (currentTeam) => {
    const [p1, p2] = currentTeam;
    let p1GamesSince = 0;
    let p2GamesSince = 0;

    for (let i = sessionState.matchHistory.length - 1; i >= 0; i--) {
      const game = sessionState.matchHistory[i];
      let gameTeam1 = game.team1;
      let gameTeam2 = game.team2;
      if ((!Array.isArray(gameTeam1) || !Array.isArray(gameTeam2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
        [gameTeam1, gameTeam2] = game.teams;
      }
      if (!Array.isArray(gameTeam1) || !Array.isArray(gameTeam2)) {
        continue;
      }

      const p1InGame = gameTeam1.includes(p1) || gameTeam2.includes(p1);
      const p2InGame = gameTeam1.includes(p2) || gameTeam2.includes(p2);
      if (p1InGame) p1GamesSince += 1;
      if (p2InGame) p2GamesSince += 1;

      const sameTeamInGame =
        (gameTeam1.includes(p1) && gameTeam1.includes(p2)) ||
        (gameTeam2.includes(p1) && gameTeam2.includes(p2));

      if (sameTeamInGame) {
        return p1GamesSince <= windowSize || p2GamesSince <= windowSize;
      }

      if (p1GamesSince > windowSize && p2GamesSince > windowSize) {
        return false;
      }
    }

    return false;
  };

  if (hasRecentRepeatForTeam(team1)) {
    const [p1, p2] = team1;
    if (!partnershipMap.has(p1) || partnershipMap.get(p1) !== p2) {
      debugLog('🚫 RECENT REPEAT TEAM - HARD BLOCK:', {
        team: team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        context: `Played together within last ${windowSize} games for one of the players`,
      });
      return -Infinity;
    }
  }

  if (hasRecentRepeatForTeam(team2)) {
    const [p1, p2] = team2;
    if (!partnershipMap.has(p1) || partnershipMap.get(p1) !== p2) {
      debugLog('🚫 RECENT REPEAT TEAM - HARD BLOCK:', {
        team: team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + '),
        context: `Played together within last ${windowSize} games for one of the players`,
      });
      return -Infinity;
    }
  }

  return 100;
}

/**
 * Singles: block immediate back-to-back identical 1v1 (same two players).
 */
export function checkImmediateSinglesRematch(team1, team2, sessionState, hardBlocks = null, scheduledCombinations = []) {
  if (hardBlocks?.allowImmediateRepeat === true) {
    return 100;
  }
  if (!team1 || !team2 || team1.length !== 1 || team2.length !== 1) {
    return 100;
  }
  const isScheduled = scheduledCombinations?.some(scheduledCombination =>
    team1.every(id => scheduledCombination.includes(id)) &&
    team2.every(id => scheduledCombination.includes(id))
  );
  if (isScheduled) {
    return 100;
  }
  if (!sessionState?.matchHistory?.length) {
    return 100;
  }
  const last = sessionState.matchHistory[sessionState.matchHistory.length - 1];
  let g1 = last.team1;
  let g2 = last.team2;
  if ((!Array.isArray(g1) || !Array.isArray(g2)) && Array.isArray(last.teams) && last.teams.length >= 2) {
    [g1, g2] = last.teams;
  }
  if (!Array.isArray(g1) || !Array.isArray(g2) || g1.length !== 1 || g2.length !== 1) {
    return 100;
  }
  const lastSet = new Set([...g1, ...g2]);
  const proposedSet = new Set([...team1, ...team2]);
  if (lastSet.size === 2 && proposedSet.size === 2 && [...proposedSet].every(id => lastSet.has(id))) {
    debugLog('🚫 IMMEDIATE SINGLES REMATCH - HARD BLOCK');
    return -Infinity;
  }
  return 100;
}

/**
 * Singles: block same 1v1 pairing if it occurred within repeatTeamWindow games ago.
 */
export function checkRecentSinglesRematch(team1, team2, sessionState, hardBlocks = null, scheduledCombinations = []) {
  if (hardBlocks?.allowRecentRepeat === true) {
    return 100;
  }
  if (!team1 || !team2 || team1.length !== 1 || team2.length !== 1) {
    return 100;
  }
  const isScheduled = scheduledCombinations?.some(scheduledCombination =>
    team1.every(id => scheduledCombination.includes(id)) &&
    team2.every(id => scheduledCombination.includes(id))
  );
  if (isScheduled) {
    return 100;
  }
  const windowSize = hardBlocks?.repeatTeamWindow ?? MATCHMAKING_CONFIG.repeatTeamWindow ?? 6;
  if (!sessionState?.matchHistory?.length) {
    return 100;
  }
  const a = team1[0];
  const b = team2[0];
  const proposed = new Set([a, b]);
  for (let i = sessionState.matchHistory.length - 1; i >= 0; i--) {
    const game = sessionState.matchHistory[i];
    let g1 = game.team1;
    let g2 = game.team2;
    if ((!Array.isArray(g1) || !Array.isArray(g2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
      [g1, g2] = game.teams;
    }
    if (!Array.isArray(g1) || !Array.isArray(g2) || g1.length !== 1 || g2.length !== 1) {
      continue;
    }
    const past = new Set([...g1, ...g2]);
    if (past.size === 2 && proposed.size === 2 && [...proposed].every(id => past.has(id))) {
      const gamesAgo = sessionState.matchHistory.length - 1 - i;
      if (gamesAgo <= windowSize) {
        debugLog('🚫 RECENT SINGLES REMATCH - HARD BLOCK', { gamesAgo, windowSize });
        return -Infinity;
      }
      return 100;
    }
  }
  return 100;
}

/**
 * Check if cooldown should filter out players
 * Returns -Infinity if any player fails cooldown (unless relaxed via hardBlocks)
 * 
 * Session-level setting determines if cooldown is enabled
 * Cooldown is always tracked but only enforced if enabled
 * 
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Set} playersPassedCooldown - Set of player IDs who passed cooldown
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 * @param {boolean} cooldownEnabled - Session setting for whether cooldown is enabled
 * @returns {number} 100 if allowed, -Infinity if blocked
 */
export function checkCooldownHardBlock(team1, team2, playersPassedCooldown, hardBlocks = null, cooldownEnabled = true) {
  // If cooldown disabled at session level, skip
  if (!cooldownEnabled) {
    return 100;
  }
  
  // If relaxed at high level, skip check
  if (hardBlocks?.enforceCooldown === false) {
    debugLog('✅ Cooldown RELAXED via hardBlocks');
    return 100;
  }
  
  // If no cooldown data, skip
  if (!playersPassedCooldown || playersPassedCooldown.size === 0) {
    return 100; // No cooldown tracking
  }
  
  const allPlayerIds = [...team1, ...team2];
  const failedPlayers = [];
  
  for (const id of allPlayerIds) {
    if (!playersPassedCooldown.has(id)) {
      failedPlayers.push(id);
    }
  }
  
  if (failedPlayers.length > 0) {
    return -Infinity; // Block
  }
  
  return 100; // All players passed cooldown
}

function setsMatchAsUnorderedTeams(aSet, bSet) {
  if (aSet.size !== bSet.size) return false;
  for (const id of aSet) {
    if (!bSet.has(id)) return false;
  }
  return true;
}

/**
 * Same four individuals about to play again with different partnered sides than their
 * personal last doubles game is blocked (per-player view of matchHistory, not session index).
 * Relaxes when {@link hardBlocks.allowExactRepeat} is true (same tier as identical repeat-game).
 *
 * @param {Array<string>} team1
 * @param {Array<string>} team2
 * @param {Object} sessionState
 * @param {Object|null} hardBlocks
 * @param {Array} scheduledCombinations
 * @returns {number} 100 if allowed; -Infinity if blocked
 */
export function checkSameFourPodAfterPersonalLastGame(
  team1,
  team2,
  sessionState,
  hardBlocks = null,
  scheduledCombinations = [],
) {
  if (hardBlocks?.allowExactRepeat === true) {
    return 100;
  }
  // Doubles 2v2 only
  if (!team1 || !team2 || team1.length !== 2 || team2.length !== 2) {
    return 100;
  }

  const isScheduled = scheduledCombinations?.some(scheduledCombination =>
    team1.every(id => scheduledCombination.includes(id)) &&
    team2.every(id => scheduledCombination.includes(id)),
  );
  if (isScheduled) {
    return 100;
  }

  const matchHistory = sessionState?.matchHistory;
  if (!matchHistory || matchHistory.length === 0) {
    return 100;
  }

  const proposed = new Set([...team1, ...team2]);
  if (proposed.size !== 4) {
    return 100;
  }

  for (const playerId of proposed) {
    let lastRosterForPlayer = null;
    for (let i = matchHistory.length - 1; i >= 0; i--) {
      const game = matchHistory[i];
      let gameTeam1;
      let gameTeam2;
      if (game.teams && Array.isArray(game.teams) && game.teams.length >= 2) {
        [gameTeam1, gameTeam2] = game.teams;
      } else if (game.team1 && game.team2) {
        gameTeam1 = game.team1;
        gameTeam2 = game.team2;
      } else {
        continue;
      }
      if (gameTeam1.length !== 2 || gameTeam2.length !== 2) {
        continue;
      }
      const roster = new Set([...gameTeam1, ...gameTeam2]);
      if (roster.size !== 4 || !roster.has(playerId)) {
        continue;
      }
      lastRosterForPlayer = roster;
      break;
    }

    if (!lastRosterForPlayer || !setsMatchAsUnorderedTeams(proposed, lastRosterForPlayer)) {
      return 100;
    }
  }

  return -Infinity;
}

/**
 * Check if this exact game (same 4 players, same teams) has been played before
 * Returns true if this is an exact repeat
 * @param {Array} team1 - Team 1 player IDs
 * @param {Array} team2 - Team 2 player IDs
 * @param {Object} sessionState - Session state with matchHistory
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 */
export function checkExactRepeatGame(team1, team2, sessionState, hardBlocks = null, scheduledCombinations = []) {
  // If exact repeats are allowed (relaxed), return false (not a problem)
  if (hardBlocks?.allowExactRepeat === true) {
    return false;
  }

  const isScheduled = scheduledCombinations?.some(scheduledCombination =>
    team1.every(id => scheduledCombination.includes(id)) &&
    team2.every(id => scheduledCombination.includes(id))
  );
  if (isScheduled) {
    return false;
  }
  
  if (!sessionState.matchHistory || sessionState.matchHistory.length === 0) {
    return false;
  }
  
  const team1Set = new Set(team1);
  const team2Set = new Set(team2);
  
  // Check each past game
  for (const game of sessionState.matchHistory) {
    let gameTeam1, gameTeam2;
    
    // Support both old format (teams: [team1, team2]) and new format (team1, team2)
    if (game.teams && Array.isArray(game.teams) && game.teams.length >= 2) {
      [gameTeam1, gameTeam2] = game.teams;
    } else if (game.team1 && game.team2) {
      gameTeam1 = game.team1;
      gameTeam2 = game.team2;
    } else {
      continue; // Skip malformed games
    }
    
    const gameTeam1Set = new Set(gameTeam1);
    const gameTeam2Set = new Set(gameTeam2);
    
    // Check if teams match exactly (Team1 vs Team2 or Team2 vs Team1)
    const team1Match = (
      team1Set.size === gameTeam1Set.size &&
      [...team1Set].every(id => gameTeam1Set.has(id))
    );
    const team2Match = (
      team2Set.size === gameTeam2Set.size &&
      [...team2Set].every(id => gameTeam2Set.has(id))
    );
    
    if (team1Match && team2Match) {
      return true; // Exact repeat: same teams
    }
    
    // Check reverse (Team1 was Team2 last time)
    const team1MatchReverse = (
      team1Set.size === gameTeam2Set.size &&
      [...team1Set].every(id => gameTeam2Set.has(id))
    );
    const team2MatchReverse = (
      team2Set.size === gameTeam1Set.size &&
      [...team2Set].every(id => gameTeam1Set.has(id))
    );
    
    if (team1MatchReverse && team2MatchReverse) {
      return true; // Exact repeat: teams swapped sides
    }
  }
  
  return false;
}

/**
 * Combine all dimension scores into final weighted score
 * @param {Object} dimensionScores - Scores for each dimension (0-100 or -Infinity)
 * @param {Object} scoringConfig - Optional scoring configuration (uses DEFAULT_SCORING_CONFIG if not provided)
 * @returns {number} Final weighted score (0-100) or -Infinity for hard blocks
 */
export function calculateFinalScore(dimensionScores, scoringConfig = null) {
  const { fairness, eloBalance, interactions, gameType, categories } = dimensionScores;

  if (gameType === -Infinity || categories === -Infinity || interactions === -Infinity) {
    return -Infinity;
  }
  
  // Use provided config or fall back to default
  const config = scoringConfig || DEFAULT_SCORING_CONFIG;
  const weights = config.weights;
  
  const finalScore = (fairness * weights.fairness) +
                     (eloBalance * weights.eloBalance) +
                     (interactions * weights.interactions) +
                     (gameType * (weights.gameType ?? 0.20));
  
  return finalScore;
}

/**
 * Validate that a scoring configuration is valid
 * @param {Object} config - Scoring configuration to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateScoringConfig(config) {
  const errors = [];
  
  if (!config || !config.weights) {
    errors.push('Config must have a weights object');
    return { valid: false, errors };
  }
  
  const weights = config.weights;
  const requiredWeights = ['fairness', 'eloBalance', 'interactions', 'gameType'];
  
  // Check all required weights exist
  for (const weight of requiredWeights) {
    if (typeof weights[weight] !== 'number') {
      errors.push(`Missing or invalid weight: ${weight}`);
    } else if (weights[weight] < 0 || weights[weight] > 1) {
      errors.push(`Weight ${weight} must be between 0 and 1 (got ${weights[weight]})`);
    }
  }
  
  // Check weights sum to 1.0 (within tolerance for floating point)
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  if (Math.abs(sum - 1.0) > 0.01) {
    errors.push(`Weights must sum to 1.0 (got ${sum.toFixed(3)})`);
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Helper: Get recent interactions between two players
 */
function getRecentInteractions(player1Id, player2Id, sessionState, windowSize) {
  if (!sessionState.matchHistory || sessionState.matchHistory.length === 0) {
    return [];
  }

  const interactions = [];
  let player1GameIndex = 0;
  let player2GameIndex = 0;

  sessionState.matchHistory.forEach(game => {
    let team1 = game.team1;
    let team2 = game.team2;
    if ((!Array.isArray(team1) || !Array.isArray(team2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
      [team1, team2] = game.teams;
    }
    if (!Array.isArray(team1) || !Array.isArray(team2)) {
      return;
    }

    const allPlayers = [...team1, ...team2];
    const player1InGame = allPlayers.includes(player1Id);
    const player2InGame = allPlayers.includes(player2Id);

    if (player1InGame) player1GameIndex += 1;
    if (player2InGame) player2GameIndex += 1;

    if (player1InGame && player2InGame) {
      const sameTeam = (team1.includes(player1Id) && team1.includes(player2Id)) ||
                       (team2.includes(player1Id) && team2.includes(player2Id));
      interactions.push({
        player1GameIndex,
        player2GameIndex,
        sameTeam,
      });
    }
  });

  if (interactions.length === 0) {
    return [];
  }

  const player1Max = interactions[interactions.length - 1].player1GameIndex;
  const player2Max = interactions[interactions.length - 1].player2GameIndex;
  const player1Cutoff = Math.max(1, player1Max - windowSize + 1);
  const player2Cutoff = Math.max(1, player2Max - windowSize + 1);

  return interactions.filter(interaction =>
    interaction.player1GameIndex >= player1Cutoff ||
    interaction.player2GameIndex >= player2Cutoff
  );
}

/**
 * Helper: Calculate recency-weighted interaction score
 */
function calculateRecencyWeight(interactions, decayFactor) {
  if (interactions.length === 0) return 0;
  
  let weight = 0;
  interactions.forEach((interaction, index) => {
    // Most recent interaction has full weight, older ones decay
    const gamesAgo = interactions.length - index;
    const recencyMultiplier = Math.pow(decayFactor, gamesAgo - 1);
    weight += recencyMultiplier;
  });
  
  return weight;
}

/**
 * Helper: Count consecutive interactions (from most recent game backwards)
 */
function countConsecutiveInteractions(interactions) {
  if (interactions.length === 0) return 0;

  const sorted = [...interactions].sort((a, b) => {
    const aIndex = Math.max(a.player1GameIndex || 0, a.player2GameIndex || 0);
    const bIndex = Math.max(b.player1GameIndex || 0, b.player2GameIndex || 0);
    return bIndex - aIndex;
  });

  const countForIndex = (key) => {
    if (!sorted[0][key]) return 0;
    let consecutive = 0;
    let expectedIndex = sorted[0][key];
    for (const interaction of sorted) {
      const value = interaction[key];
      if (!value) continue;
      if (value === expectedIndex) {
        consecutive += 1;
        expectedIndex -= 1;
      } else {
        break;
      }
    }
    return consecutive;
  };

  const player1Consecutive = countForIndex('player1GameIndex');
  const player2Consecutive = countForIndex('player2GameIndex');

  return Math.max(player1Consecutive, player2Consecutive);
}
